import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

/** Command Center > Modules > Community (Joe). Staff only; the agency router gates it. */

export interface CommunitySettings {
  locationId: string;
  practiceId: string;
  practiceType: string;
  referralDirection: 'in' | 'out';
  rationale: string;
  radiusMi: number;
  enabledCategories: string[];
  enabledEventTypes: string[];
  eventFeedUrls: string[];
  picksPerWeek: number;
  referralCheckinOn: boolean;
  peerNetworking: boolean;
  updatedAt: string;
}

export interface CommunitySettingsView {
  featureEnabled: boolean;
  joeActive: boolean;
  settings: CommunitySettings;
  options: {
    practiceTypes: Array<{ key: string; label: string; vertical: string; verticalLabel: string; recommended: boolean; rationale: string }>;
    radii: readonly number[];
    categories: Array<{ key: string; label: string; kind: 'partner' | 'nontraditional_partner'; seeded: boolean }>;
    eventTypes: Array<{ key: string; label: string; seeded: boolean }>;
  };
}

export interface CommunitySettingsPatch {
  practiceType?: string;
  radiusMi?: number;
  enabledCategories?: string[];
  enabledEventTypes?: string[];
  eventFeedUrls?: string[];
  picksPerWeek?: number;
  referralCheckinOn?: boolean;
  peerNetworking?: boolean;
}

export interface ScoutRunRow {
  id: string;
  weekOf: string;
  trigger: string;
  status: string;
  candidates: number;
  picks: number;
  note: string | null;
  sources: Record<string, string> | null;
  startedAt: string;
  finishedAt: string | null;
}

/** A public calendar Joe found (or staff added) for a location; proposed until an admin approves it. */
export interface EventSourceRow {
  id: string;
  url: string;
  kind: 'ical' | 'rss' | 'jsonld' | 'gcal' | string;
  orgName: string | null;
  orgWebsite: string | null;
  discoveredVia: 'discovery' | 'owner_event' | 'staff' | string;
  status: 'proposed' | 'approved' | 'declined';
  eventsFound: number;
  sampleTitles: string[];
  lastCheckedAt: string | null;
  decidedAt: string | null;
  createdAt: string;
}

export interface FeedPreview {
  count: number;
  events: Array<{ title: string; start: string; organizer: string | null; format: string; hasCost: boolean; location: string | null; url: string | null }>;
}

const unwrap = <T,>(res: { data: ApiResponse<T> }): T => res.data.data;
const base = (practiceId: string, locationId: string) => `/agency/community/practices/${practiceId}/locations/${locationId}`;

export const communityAdminApi = {
  getSettings: (practiceId: string, locationId: string) =>
    apiClient.get<ApiResponse<CommunitySettingsView>>(`${base(practiceId, locationId)}/settings`).then(unwrap),
  updateSettings: (practiceId: string, locationId: string, patch: CommunitySettingsPatch) =>
    apiClient.patch<ApiResponse<CommunitySettings>>(`${base(practiceId, locationId)}/settings`, patch).then(unwrap),
  runScoutNow: (practiceId: string, locationId: string) =>
    apiClient.post<ApiResponse<{ queued: boolean }>>(`${base(practiceId, locationId)}/scout/run`).then(unwrap),
  listRuns: (practiceId: string, locationId: string) =>
    apiClient.get<ApiResponse<ScoutRunRow[]>>(`${base(practiceId, locationId)}/scout/runs`).then(unwrap),
  testFeed: (url: string) => apiClient.post<ApiResponse<FeedPreview>>('/agency/community/feeds/test', { url }).then(unwrap),
  listEventSources: (practiceId: string, locationId: string) =>
    apiClient.get<ApiResponse<EventSourceRow[]>>(`${base(practiceId, locationId)}/event-sources`).then(unwrap),
  addEventSource: (practiceId: string, locationId: string, url: string) =>
    apiClient.post<ApiResponse<EventSourceRow>>(`${base(practiceId, locationId)}/event-sources`, { url }).then(unwrap),
  discoverCalendars: (practiceId: string, locationId: string) =>
    apiClient.post<ApiResponse<{ queued: boolean }>>(`${base(practiceId, locationId)}/event-sources/discover`).then(unwrap),
  decideEventSource: (practiceId: string, locationId: string, id: string, decision: 'approve' | 'decline') =>
    apiClient.post<ApiResponse<EventSourceRow>>(`${base(practiceId, locationId)}/event-sources/${id}/${decision}`).then(unwrap),
};
